/**
 * Event Admin Page — /event/admin
 * Shows all Stripe purchases for the Business Event + DB registrations.
 * Sacred Modern design matching the AYU aesthetic.
 */
import { useState, useRef, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { useClientAuth } from "@/contexts/ClientAuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft,
  RefreshCw,
  Users,
  CreditCard,
  Mail,
  Phone,
  Calendar,
  DollarSign,
  CheckCircle2,
  Clock,
  XCircle,
  Download,
  Search,
  Loader2,
  Ticket,
  Send,
  Trash2,
  StickyNote,
  Save,
  X,
  MessageSquare,
} from "lucide-react";

export default function EventAdmin() {
  const { user, loading: authLoading } = useAuth();
  const { client: clientUser, loading: clientLoading } = useClientAuth();
  const isManusAdmin = !!(user && user.role === "admin");
  const isClientAdmin = !!(clientUser && clientUser.isAdmin);
  const isAuthenticated = isManusAdmin || isClientAdmin;
  const isStillLoading = authLoading || clientLoading;
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState<"stripe" | "db">("stripe");

  // Stripe purchases
  const {
    data: stripeData,
    isLoading: stripeLoading,
    refetch: refetchStripe,
  } = trpc.event.adminListStripePurchases.useQuery(undefined, {
    enabled: isAuthenticated,
  });

  // DB registrations
  const {
    data: dbData,
    isLoading: dbLoading,
    refetch: refetchDb,
  } = trpc.event.adminListRegistrations.useQuery(undefined, {
    enabled: isAuthenticated,
  });

  // Admin actions
  const updateStatus = trpc.event.adminUpdatePaymentStatus.useMutation({
    onSuccess: () => {
      toast.success("Status aktualisiert");
      refetchDb();
    },
    onError: (err) => toast.error(err.message),
  });

  const sendTicket = trpc.event.adminSendTicket.useMutation({
    onSuccess: () => {
      toast.success("Ticket-E-Mail gesendet");
      refetchDb();
    },
    onError: (err) => toast.error(err.message),
  });

  const deleteReg = trpc.event.adminDeleteRegistration.useMutation({
    onSuccess: () => {
      toast.success("Registrierung gelöscht");
      refetchDb();
    },
    onError: (err) => toast.error(err.message),
  });

  const updateNotes = trpc.event.adminUpdateNotes.useMutation({
    onSuccess: () => {
      toast.success("Notiz gespeichert");
      refetchDb();
    },
    onError: (err) => toast.error(err.message),
  });

  // Auth guard
  if (isStillLoading) {
    return (
      <div className="min-h-screen bg-[#1a1510] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#c9a96e] animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    window.location.replace("/login?redirect=/event/admin");
    return null;
  }

  const handleRefresh = () => {
    refetchStripe();
    refetchDb();
    toast.success("Daten werden aktualisiert...");
  };

  // Filter logic
  const filteredStripePurchases = (stripeData?.purchases || []).filter(
    (p) =>
      p.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.customerEmail.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredDbRegistrations = (dbData?.registrations || []).filter(
    (r) =>
      `${r.firstName} ${r.lastName}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      r.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // CSV Export
  const exportCSV = () => {
    const rows =
      activeTab === "stripe"
        ? filteredStripePurchases.map((p) => ({
            Name: p.customerName,
            Email: p.customerEmail,
            Telefon: p.phone,
            Betrag: `${p.amountTotal} ${p.currency}`,
            Status: p.paymentStatus,
            Datum: new Date(p.createdAt).toLocaleDateString("de-DE"),
          }))
        : filteredDbRegistrations.map((r) => ({
            Name: `${r.firstName} ${r.lastName}`,
            Email: r.email,
            Telefon: r.phone || "",
            Bewerbung: r.applicationText || "",
            Notizen: r.adminNotes || "",
            Status: r.paymentStatus,
            Datum: r.createdAt
              ? new Date(r.createdAt).toLocaleDateString("de-DE")
              : "",
          }));

    if (rows.length === 0) return toast.error("Keine Daten zum Exportieren");

    const headers = Object.keys(rows[0]);
    const csv = [
      headers.join(","),
      ...rows.map((r) =>
        headers
          .map((h) => `"${String((r as any)[h]).replace(/"/g, '""')}"`)
          .join(",")
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `event-${activeTab}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exportiert");
  };

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  const statusBadge = (status: string) => {
    const styles: Record<string, string> = {
      paid: "bg-emerald-900/40 text-emerald-400 border-emerald-700/50",
      pending: "bg-amber-900/40 text-amber-400 border-amber-700/50",
      failed: "bg-red-900/40 text-red-400 border-red-700/50",
      refunded: "bg-blue-900/40 text-blue-400 border-blue-700/50",
    };
    const icons: Record<string, typeof CheckCircle2> = {
      paid: CheckCircle2,
      pending: Clock,
      failed: XCircle,
      refunded: RefreshCw,
    };
    const Icon = icons[status] || Clock;
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.pending}`}
      >
        <Icon className="w-3 h-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-[#1a1510]">
      {/* Header */}
      <header className="border-b border-[#c9a96e]/15 bg-[#1e1b17]/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a
              href="/event"
              className="text-[#a09880] hover:text-[#c9a96e] transition"
            >
              <ArrowLeft className="w-5 h-5" />
            </a>
            <div>
              <h1 className="text-xl font-bold text-[#e8dcc8] flex items-center gap-2">
                <Ticket className="w-5 h-5 text-[#c9a96e]" />
                Event Admin
              </h1>
              <p className="text-sm text-[#a09880]">
                Annam's Business Event — 24. Mai 2026
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              className="border-[#c9a96e]/30 text-[#c9a96e] hover:bg-[#c9a96e]/10"
            >
              <RefreshCw className="w-4 h-4 mr-1.5" />
              Aktualisieren
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={exportCSV}
              className="border-[#c9a96e]/30 text-[#c9a96e] hover:bg-[#c9a96e]/10"
            >
              <Download className="w-4 h-4 mr-1.5" />
              CSV
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            icon={CreditCard}
            label="Stripe-Käufe"
            value={stripeData?.total ?? "—"}
            loading={stripeLoading}
          />
          <StatCard
            icon={DollarSign}
            label="Umsatz (Stripe)"
            value={
              stripeData?.totalRevenue
                ? `${stripeData.totalRevenue} EUR`
                : "—"
            }
            loading={stripeLoading}
          />
          <StatCard
            icon={Users}
            label="DB-Registrierungen"
            value={dbData?.stats?.total ?? "—"}
            loading={dbLoading}
          />
          <StatCard
            icon={CheckCircle2}
            label="Bezahlt (DB)"
            value={dbData?.stats?.paid ?? "—"}
            loading={dbLoading}
            accent
          />
        </div>

        {/* Tab Switcher + Search */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex bg-[#252219] rounded-lg p-1 border border-[#c9a96e]/10">
            <button
              onClick={() => setActiveTab("stripe")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                activeTab === "stripe"
                  ? "bg-[#c9a96e]/20 text-[#c9a96e]"
                  : "text-[#a09880] hover:text-[#e8dcc8]"
              }`}
            >
              <CreditCard className="w-4 h-4 inline mr-1.5" />
              Stripe-Käufe ({stripeData?.total ?? 0})
            </button>
            <button
              onClick={() => setActiveTab("db")}
              className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                activeTab === "db"
                  ? "bg-[#c9a96e]/20 text-[#c9a96e]"
                  : "text-[#a09880] hover:text-[#e8dcc8]"
              }`}
            >
              <Users className="w-4 h-4 inline mr-1.5" />
              DB-Registrierungen ({dbData?.stats?.total ?? 0})
            </button>
          </div>
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#a09880]" />
            <input
              type="text"
              placeholder="Name oder E-Mail suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-[#252219] border border-[#c9a96e]/10 rounded-lg text-[#e8dcc8] placeholder-[#6b6050] text-sm focus:outline-none focus:border-[#c9a96e]/30"
            />
          </div>
        </div>

        {/* Stripe Purchases Table */}
        {activeTab === "stripe" && (
          <div className="bg-[#1e1b17] border border-[#c9a96e]/10 rounded-xl overflow-hidden">
            {stripeLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 text-[#c9a96e] animate-spin" />
              </div>
            ) : filteredStripePurchases.length === 0 ? (
              <div className="text-center py-16">
                <CreditCard className="w-12 h-12 text-[#6b6050] mx-auto mb-3" />
                <p className="text-[#a09880]">Keine Stripe-Käufe gefunden</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#c9a96e]/10">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#a09880] uppercase tracking-wider">
                        Kunde
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#a09880] uppercase tracking-wider hidden md:table-cell">
                        E-Mail
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-[#a09880] uppercase tracking-wider hidden lg:table-cell">
                        Telefon
                      </th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-[#a09880] uppercase tracking-wider">
                        Betrag
                      </th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-[#a09880] uppercase tracking-wider">
                        Status
                      </th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-[#a09880] uppercase tracking-wider hidden sm:table-cell">
                        Datum
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStripePurchases.map((p, i) => (
                      <tr
                        key={p.id}
                        className={`border-b border-[#c9a96e]/5 hover:bg-[#c9a96e]/5 transition ${
                          i % 2 === 0 ? "bg-[#1e1b17]" : "bg-[#1a1814]"
                        }`}
                      >
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-[#e8dcc8]">
                            {p.customerName}
                          </div>
                          <div className="text-xs text-[#a09880] md:hidden">
                            {p.customerEmail}
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <a
                            href={`mailto:${p.customerEmail}`}
                            className="text-sm text-[#c9a96e] hover:underline flex items-center gap-1.5"
                          >
                            <Mail className="w-3.5 h-3.5" />
                            {p.customerEmail}
                          </a>
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <span className="text-sm text-[#a09880] flex items-center gap-1.5">
                            <Phone className="w-3.5 h-3.5" />
                            {p.phone}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-sm font-semibold text-[#e8dcc8]">
                            {p.amountTotal} {p.currency}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {statusBadge(p.paymentStatus)}
                        </td>
                        <td className="px-4 py-3 text-right hidden sm:table-cell">
                          <span className="text-xs text-[#a09880] flex items-center gap-1.5 justify-end">
                            <Calendar className="w-3.5 h-3.5" />
                            {formatDate(p.createdAt)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* DB Registrations — Card Layout with Notes */}
        {activeTab === "db" && (
          <div className="space-y-4">
            {dbLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 text-[#c9a96e] animate-spin" />
              </div>
            ) : filteredDbRegistrations.length === 0 ? (
              <div className="text-center py-16 bg-[#1e1b17] border border-[#c9a96e]/10 rounded-xl">
                <Users className="w-12 h-12 text-[#6b6050] mx-auto mb-3" />
                <p className="text-[#a09880]">Keine Registrierungen gefunden</p>
              </div>
            ) : (
              filteredDbRegistrations.map((r) => (
                <RegistrationCard
                  key={r.id}
                  registration={r}
                  formatDate={formatDate}
                  statusBadge={statusBadge}
                  onUpdateStatus={(status) =>
                    updateStatus.mutate({
                      registrationId: r.id,
                      paymentStatus: status,
                    })
                  }
                  onSendTicket={() =>
                    sendTicket.mutate({ registrationId: r.id })
                  }
                  onDelete={() => {
                    if (
                      confirm(
                        `Registrierung von ${r.firstName} ${r.lastName} wirklich löschen?`
                      )
                    ) {
                      deleteReg.mutate({ registrationId: r.id });
                    }
                  }}
                  onSaveNotes={(notes) =>
                    updateNotes.mutate({
                      registrationId: r.id,
                      notes,
                    })
                  }
                  isSavingNotes={updateNotes.isPending}
                />
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
}

/** Registration Card with inline notes */
function RegistrationCard({
  registration: r,
  formatDate,
  statusBadge,
  onUpdateStatus,
  onSendTicket,
  onDelete,
  onSaveNotes,
  isSavingNotes,
}: {
  registration: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    applicationText: string;
    language: string;
    paymentStatus: string;
    ticketSent: boolean;
    adminNotes: string | null;
    createdAt: any;
  };
  formatDate: (d: string) => string;
  statusBadge: (s: string) => React.ReactNode;
  onUpdateStatus: (status: "pending" | "paid" | "failed" | "refunded") => void;
  onSendTicket: () => void;
  onDelete: () => void;
  onSaveNotes: (notes: string) => void;
  isSavingNotes: boolean;
}) {
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [notesText, setNotesText] = useState(r.adminNotes || "");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setNotesText(r.adminNotes || "");
  }, [r.adminNotes]);

  useEffect(() => {
    if (isEditingNotes && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.selectionStart = textareaRef.current.value.length;
    }
  }, [isEditingNotes]);

  const handleSaveNotes = () => {
    onSaveNotes(notesText);
    setIsEditingNotes(false);
  };

  const handleCancelNotes = () => {
    setNotesText(r.adminNotes || "");
    setIsEditingNotes(false);
  };

  const dateStr = r.createdAt
    ? formatDate(
        typeof r.createdAt === "string"
          ? r.createdAt
          : new Date(r.createdAt).toISOString()
      )
    : "—";

  return (
    <div className="bg-[#1e1b17] border border-[#c9a96e]/10 rounded-xl overflow-hidden hover:border-[#c9a96e]/20 transition">
      {/* Main Info Row */}
      <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-4">
        {/* Name + Contact */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <h3 className="text-base font-semibold text-[#e8dcc8] truncate">
              {r.firstName} {r.lastName}
            </h3>
            {statusBadge(r.paymentStatus)}
            {r.ticketSent && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-emerald-900/30 text-emerald-400 border border-emerald-700/30">
                <Ticket className="w-3 h-3" />
                Gesendet
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#a09880]">
            <a
              href={`mailto:${r.email}`}
              className="text-[#c9a96e] hover:underline flex items-center gap-1"
            >
              <Mail className="w-3.5 h-3.5" />
              {r.email}
            </a>
            <span className="flex items-center gap-1">
              <Phone className="w-3.5 h-3.5" />
              {r.phone || "—"}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              {dateStr}
            </span>
          </div>
          {r.applicationText && (
            <p className="text-xs text-[#7d7060] mt-2 line-clamp-2">
              {r.applicationText}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1.5 shrink-0">
          {r.paymentStatus === "pending" && (
            <button
              onClick={() => onUpdateStatus("paid")}
              className="p-2 rounded-lg hover:bg-emerald-900/30 text-emerald-500 transition"
              title="Als bezahlt markieren"
            >
              <CheckCircle2 className="w-4 h-4" />
            </button>
          )}
          {r.paymentStatus === "paid" && !r.ticketSent && (
            <button
              onClick={onSendTicket}
              className="p-2 rounded-lg hover:bg-[#c9a96e]/20 text-[#c9a96e] transition"
              title="Ticket-E-Mail senden"
            >
              <Send className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => setIsEditingNotes(!isEditingNotes)}
            className={`p-2 rounded-lg transition ${
              isEditingNotes
                ? "bg-[#c9a96e]/20 text-[#c9a96e]"
                : r.adminNotes
                  ? "hover:bg-[#c9a96e]/10 text-[#c9a96e]"
                  : "hover:bg-[#c9a96e]/10 text-[#6b6050]"
            }`}
            title={r.adminNotes ? "Notiz bearbeiten" : "Notiz hinzufügen"}
          >
            <StickyNote className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-2 rounded-lg hover:bg-red-900/30 text-red-500 transition"
            title="Löschen"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Notes Section — shown when there are notes or editing */}
      {(r.adminNotes || isEditingNotes) && (
        <div className="border-t border-[#c9a96e]/10 px-4 py-3 bg-[#1a1814]">
          {isEditingNotes ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-1">
                <MessageSquare className="w-3.5 h-3.5 text-[#c9a96e]" />
                <span className="text-xs font-medium text-[#c9a96e]">
                  Notiz bearbeiten
                </span>
              </div>
              <textarea
                ref={textareaRef}
                value={notesText}
                onChange={(e) => setNotesText(e.target.value)}
                placeholder="Notiz hinzufügen... (z.B. VIP, Sonderwünsche, Kontaktinfo)"
                rows={3}
                className="w-full bg-[#252219] border border-[#c9a96e]/20 rounded-lg px-3 py-2 text-sm text-[#e8dcc8] placeholder-[#6b6050] focus:outline-none focus:border-[#c9a96e]/40 resize-none"
              />
              <div className="flex items-center gap-2 justify-end">
                <button
                  onClick={handleCancelNotes}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs text-[#a09880] hover:bg-[#252219] transition"
                >
                  <X className="w-3 h-3" />
                  Abbrechen
                </button>
                <button
                  onClick={handleSaveNotes}
                  disabled={isSavingNotes}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs bg-[#c9a96e]/20 text-[#c9a96e] hover:bg-[#c9a96e]/30 transition disabled:opacity-50"
                >
                  {isSavingNotes ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Save className="w-3 h-3" />
                  )}
                  Speichern
                </button>
              </div>
            </div>
          ) : (
            <div
              className="flex items-start gap-2 cursor-pointer group"
              onClick={() => setIsEditingNotes(true)}
            >
              <MessageSquare className="w-3.5 h-3.5 text-[#c9a96e]/60 mt-0.5 shrink-0" />
              <p className="text-sm text-[#a09880] group-hover:text-[#e8dcc8] transition whitespace-pre-wrap">
                {r.adminNotes}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Stat Card Component */
function StatCard({
  icon: Icon,
  label,
  value,
  loading,
  accent,
}: {
  icon: typeof Users;
  label: string;
  value: string | number;
  loading?: boolean;
  accent?: boolean;
}) {
  return (
    <div
      className={`rounded-xl border p-4 ${
        accent
          ? "bg-[#c9a96e]/10 border-[#c9a96e]/20"
          : "bg-[#1e1b17] border-[#c9a96e]/10"
      }`}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon
          className={`w-4 h-4 ${accent ? "text-[#c9a96e]" : "text-[#a09880]"}`}
        />
        <span className="text-xs text-[#a09880] font-medium">{label}</span>
      </div>
      {loading ? (
        <Loader2 className="w-5 h-5 text-[#c9a96e] animate-spin" />
      ) : (
        <p
          className={`text-2xl font-bold ${
            accent ? "text-[#c9a96e]" : "text-[#e8dcc8]"
          }`}
        >
          {value}
        </p>
      )}
    </div>
  );
}
